/**
 * Etapa 3 do cadastro de instalação elétrica: documentos técnicos (issue #364, D3).
 *
 * Quem pode alterar envia e exclui; quem só consulta baixa. "Vencido" é só uma marca na linha:
 * não muda a situação da instalação nem cria aviso — a comparação é de texto `YYYY-MM-DD`.
 */
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import {
  Alert,
  Box,
  Button,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { ConfirmDialog } from '../../../../../components/common/ConfirmDialog';
import { DataTable, type DataTableColumn } from '../../../../../components/common/DataTable';
import { electricalInstallationService } from '../../../../../services/electricalInstallationService';
import {
  ELECTRICAL_DOCUMENT_KINDS,
  ROTULO_DO_TIPO_DE_DOCUMENTO,
  type ElectricalDocumentKind,
  type ElectricalInstallationFile,
} from '../../../../../types/electricalInstallation';
import { OverdueChip } from '../../ElectricalChips';
import type {
  ListStepProps,
  ResultadoDoEnvio,
  StepHandle,
} from '../ElectricalInstallationWizard.types';
import { errorMessage } from '../electricalWizardValidation';
import { ehDataReal, formatarData, hojeEmTexto, MENSAGEM_DE_DATA } from './formatosDasEtapas';

/** Mesmo teto do servidor: o proxy corta a requisição em 10 MB e o multipart pesa mais que o arquivo. */
const LIMITE_DO_ARQUIVO_EM_BYTES = 9 * 1024 * 1024;

const TIPOS_ACEITOS: Readonly<Record<string, readonly string[]>> = {
  'application/pdf': ['.pdf'],
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
};

const EXTENSOES_ACEITAS = Object.values(TIPOS_ACEITOS).flat().join(',');

/** `null` quando o arquivo serve; senão, a frase que diz o motivo e o que fazer. */
const motivoDeRecusaDoArquivo = (file: File): string | null => {
  const extensao = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
  const tipoServe = TIPOS_ACEITOS[file.type]?.includes(extensao) ?? false;
  if (!tipoServe) {
    return `"${file.name}" não é um PDF, PNG ou JPEG. Só aceitamos esses três formatos: salve o documento num deles e escolha de novo.`;
  }
  if (file.size > LIMITE_DO_ARQUIVO_EM_BYTES) {
    const megas = (file.size / (1024 * 1024)).toLocaleString('pt-BR', { maximumFractionDigits: 1 });
    return `"${file.name}" tem ${megas} MB e o limite é 9 MB. Reduza o arquivo (por exemplo, exporte o PDF em menor qualidade) e escolha de novo.`;
  }
  return null;
};

const formatSize = (bytes: number): string =>
  bytes < 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024))} KB`
    : `${(bytes / (1024 * 1024)).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} MB`;

interface UploadForm {
  file: File | null;
  documentKind: ElectricalDocumentKind | '';
  title: string;
  issuedAt: string;
  validUntil: string;
}

type UploadErrors = Partial<Record<keyof UploadForm, string>>;

const EMPTY_FORM: UploadForm = {
  file: null,
  documentKind: '',
  title: '',
  issuedAt: '',
  validUntil: '',
};

const validateForm = (form: UploadForm): UploadErrors => {
  const errors: UploadErrors = {};
  if (form.file === null) errors.file = 'Escolha o arquivo do documento.';
  if (form.documentKind === '') errors.documentKind = 'Escolha o tipo do documento.';
  if (form.title.trim() === '') errors.title = 'Dê um título ao documento.';
  else if (form.title.trim().length > 200) errors.title = 'Use até 200 letras.';
  if (form.issuedAt.trim() !== '' && !ehDataReal(form.issuedAt.trim()))
    errors.issuedAt = MENSAGEM_DE_DATA;
  if (form.validUntil.trim() !== '' && !ehDataReal(form.validUntil.trim()))
    errors.validUntil = MENSAGEM_DE_DATA;
  return errors;
};

export const DocumentsStep = forwardRef<StepHandle, ListStepProps>(function DocumentsStep(
  { installationId, canEdit, onDirtyChange, onSaved },
  ref
) {
  const [files, setFiles] = useState<ElectricalInstallationFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [form, setForm] = useState<UploadForm>(EMPTY_FORM);
  const [errors, setErrors] = useState<UploadErrors>({});
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ElectricalInstallationFile | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const dirty =
    JSON.stringify({ ...form, file: form.file?.name ?? null }) !==
    JSON.stringify({ ...EMPTY_FORM });

  useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    electricalInstallationService
      .getFiles(installationId)
      .then(found => {
        if (active) setFiles(found);
      })
      .catch((err: unknown) => {
        if (active)
          setLoadError(errorMessage(err, 'Não foi possível carregar os documentos técnicos.'));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [installationId]);

  const patchForm = (patch: Partial<UploadForm>) => {
    setForm(previous => ({ ...previous, ...patch }));
    setErrors(previous => {
      const next = { ...previous };
      (Object.keys(patch) as Array<keyof UploadForm>).forEach(key => delete next[key]);
      return next;
    });
    setUploadError(null);
  };

  const handleFileChosen = (event: React.ChangeEvent<HTMLInputElement>) => {
    const chosen = event.target.files?.[0] ?? null;
    event.target.value = '';
    if (!chosen) return;
    const motivo = motivoDeRecusaDoArquivo(chosen);
    if (motivo) {
      setForm(previous => ({ ...previous, file: null }));
      setErrors(previous => ({ ...previous, file: motivo }));
      return;
    }
    patchForm({
      file: chosen,
      title: form.title.trim() === '' ? chosen.name.replace(/\.[^.]+$/, '') : form.title,
    });
  };

  const submit = useCallback(async (): Promise<ResultadoDoEnvio> => {
    const found = validateForm(form);
    setErrors(found);
    if (Object.keys(found).length > 0 || form.file === null || form.documentKind === '') {
      setUploadError('Corrija os campos marcados para enviar o documento.');
      return 'incompleto';
    }
    setUploading(true);
    setUploadError(null);
    try {
      const saved = await electricalInstallationService.uploadFile(installationId, form.file, {
        documentKind: form.documentKind,
        title: form.title.trim(),
        issuedAt: form.issuedAt.trim() === '' ? null : form.issuedAt.trim(),
        validUntil: form.validUntil.trim() === '' ? null : form.validUntil.trim(),
      });
      setFiles(previous => [saved, ...previous]);
      setForm(EMPTY_FORM);
      onSaved('Documento enviado.');
      return 'enviado';
    } catch (err: unknown) {
      // Tipo ou tamanho recusado pelo servidor (INVALID_FILE, FILE_TOO_LARGE): a frase dele já diz o motivo.
      setUploadError(errorMessage(err, 'Não foi possível enviar o documento.'));
      return 'recusado';
    } finally {
      setUploading(false);
    }
  }, [form, installationId, onSaved]);

  useImperativeHandle(
    ref,
    () => ({
      salvarPendente: async () => {
        if (!dirty) return 'enviado';
        return submit();
      },
    }),
    [dirty, submit]
  );

  const download = useCallback(
    async (file: ElectricalInstallationFile) => {
      setActionError(null);
      try {
        const blob = await electricalInstallationService.downloadFile(installationId, file.id);
        if (!(blob instanceof Blob)) throw new Error('Não foi possível baixar o documento.');
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = file.originalFileName;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
      } catch (err: unknown) {
        setActionError(errorMessage(err, 'Não foi possível baixar o documento.'));
      }
    },
    [installationId]
  );

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await electricalInstallationService.deleteFile(installationId, deleteTarget.id);
      setFiles(previous => previous.filter(file => file.id !== deleteTarget.id));
      onSaved('Documento excluído.');
      setDeleteTarget(null);
    } catch (err: unknown) {
      setDeleteError(errorMessage(err, 'Não foi possível excluir o documento.'));
    } finally {
      setDeleting(false);
    }
  };

  const columns = useMemo<DataTableColumn<ElectricalInstallationFile>[]>(() => {
    const hoje = hojeEmTexto();
    const base: DataTableColumn<ElectricalInstallationFile>[] = [
      {
        id: 'kind',
        label: 'Tipo',
        primary: true,
        render: file => (
          <Typography variant='body1' fontWeight='medium'>
            {ROTULO_DO_TIPO_DE_DOCUMENTO[file.documentKind]}
          </Typography>
        ),
      },
      {
        id: 'title',
        label: 'Título',
        render: file => (
          <Box>
            <Typography variant='body1'>{file.title}</Typography>
            <Typography variant='body2' color='text.secondary'>
              {file.originalFileName} · {formatSize(file.fileSize)}
            </Typography>
          </Box>
        ),
      },
      {
        id: 'issuedAt',
        label: 'Emissão',
        render: file => (file.issuedAt ? formatarData(file.issuedAt) : '—'),
      },
      {
        id: 'validUntil',
        label: 'Validade',
        render: file => {
          if (!file.validUntil) return '—';
          if (file.validUntil < hoje)
            return <OverdueChip label={`Vencido · ${formatarData(file.validUntil)}`} />;
          return formatarData(file.validUntil);
        },
      },
      {
        id: 'actions',
        label: 'Ações',
        align: 'center',
        render: file => (
          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
            <Tooltip title='Baixar'>
              <IconButton
                aria-label={`Baixar ${file.title}`}
                onClick={() => void download(file)}
                sx={{ width: 44, height: 44 }}
              >
                <DownloadIcon />
              </IconButton>
            </Tooltip>
            {canEdit && (
              <Tooltip title='Excluir'>
                <IconButton
                  aria-label={`Excluir ${file.title}`}
                  color='error'
                  onClick={() => {
                    setDeleteError(null);
                    setDeleteTarget(file);
                  }}
                  sx={{ width: 44, height: 44 }}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ),
      },
    ];
    return base;
  }, [canEdit, download]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    void submit();
  };

  return (
    <Box>
      <Box sx={{ maxWidth: 640, mb: 2 }}>
        <Typography variant='h6' component='h3'>
          Documentos técnicos
        </Typography>
        <Typography variant='body1' color='text.secondary'>
          {canEdit
            ? 'Guarde aqui o diagrama unifilar, os laudos e a documentação técnica desta instalação, em PDF, PNG ou JPEG de até 9 MB.'
            : 'Aqui estão os documentos técnicos desta instalação. Você pode baixar cada um.'}
        </Typography>
      </Box>

      {loadError && (
        <Alert severity='error' sx={{ mb: 2 }}>
          {loadError}
        </Alert>
      )}
      {actionError && (
        <Alert severity='error' sx={{ mb: 2 }}>
          {actionError}
        </Alert>
      )}

      {canEdit && (
        <Paper
          variant='outlined'
          component='form'
          noValidate
          sx={{ p: 2, mb: 2 }}
          onSubmit={handleSubmit}
        >
          <Typography variant='subtitle1' component='h4' fontWeight={700} sx={{ mb: 2 }}>
            Enviar um documento
          </Typography>
          {uploadError && (
            <Alert severity='error' sx={{ mb: 2 }}>
              {uploadError}
            </Alert>
          )}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
              gap: 2,
            }}
          >
            <Box sx={{ gridColumn: { md: '1 / -1' } }}>
              <Button
                component='label'
                variant='outlined'
                startIcon={<UploadFileIcon />}
                disabled={uploading}
                sx={{ minHeight: 44 }}
              >
                {form.file ? 'Trocar o arquivo' : 'Escolher o arquivo'}
                <input
                  hidden
                  type='file'
                  accept={EXTENSOES_ACEITAS}
                  aria-label='Arquivo do documento (PDF, PNG ou JPEG, até 9 MB)'
                  onChange={handleFileChosen}
                />
              </Button>
              <Typography
                variant='body2'
                color={errors.file ? 'error' : 'text.secondary'}
                role={errors.file ? 'alert' : undefined}
                sx={{ mt: 1 }}
              >
                {errors.file ??
                  (form.file
                    ? `Arquivo escolhido: ${form.file.name} (${formatSize(form.file.size)})`
                    : 'PDF, PNG ou JPEG, até 9 MB.')}
              </Typography>
            </Box>
            <TextField
              select
              required
              label='Tipo do documento'
              value={form.documentKind}
              onChange={event =>
                patchForm({ documentKind: event.target.value as ElectricalDocumentKind })
              }
              error={Boolean(errors.documentKind)}
              helperText={errors.documentKind}
              disabled={uploading}
            >
              {ELECTRICAL_DOCUMENT_KINDS.map(kind => (
                <MenuItem key={kind} value={kind} sx={{ minHeight: 44 }}>
                  {ROTULO_DO_TIPO_DE_DOCUMENTO[kind]}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              required
              label='Título'
              value={form.title}
              onChange={event => patchForm({ title: event.target.value })}
              error={Boolean(errors.title)}
              helperText={errors.title ?? 'Como você vai reconhecer o documento na lista.'}
              disabled={uploading}
            />
            <TextField
              label='Data de emissão'
              placeholder='AAAA-MM-DD'
              value={form.issuedAt}
              onChange={event => patchForm({ issuedAt: event.target.value })}
              error={Boolean(errors.issuedAt)}
              helperText={errors.issuedAt ?? 'Opcional. Ano-mês-dia, como 2026-03-12.'}
              disabled={uploading}
            />
            <TextField
              label='Validade'
              placeholder='AAAA-MM-DD'
              value={form.validUntil}
              onChange={event => patchForm({ validUntil: event.target.value })}
              error={Boolean(errors.validUntil)}
              helperText={
                errors.validUntil ??
                'Opcional. Depois dessa data o documento aparece como "Vencido".'
              }
              disabled={uploading}
            />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button type='submit' variant='contained' disabled={uploading} sx={{ minHeight: 44 }}>
              Enviar documento
            </Button>
          </Box>
        </Paper>
      )}

      <DataTable
        embedded
        columns={columns}
        rows={files}
        getRowId={file => file.id}
        loading={loading}
        emptyState={
          canEdit
            ? {
                title: 'Nenhum documento técnico guardado',
                description:
                  'Aqui ficam o diagrama unifilar, os laudos e outros documentos desta instalação, para encontrar tudo num lugar só quando precisar. Use "Enviar um documento" acima; dá para pular e voltar depois.',
              }
            : {
                title: 'Nenhum documento técnico guardado',
                description:
                  'Aqui ficam o diagrama unifilar, os laudos e outros documentos desta instalação. Nenhum foi guardado ainda.',
              }
        }
      />

      <ConfirmDialog
        open={deleteTarget !== null}
        title='Excluir este documento?'
        message={
          deleteTarget ? `"${deleteTarget.title}" sai da instalação e não dá para desfazer.` : ''
        }
        confirmLabel='Excluir documento'
        loading={deleting}
        error={deleteError}
        onConfirm={() => void confirmDelete()}
        onCancel={() => setDeleteTarget(null)}
      />
    </Box>
  );
});

export default DocumentsStep;
